import type { CommercialAccessDto } from "@madamuh/domain";

export const COMMERCIAL_ACCESS_BOUNDARY_SAFETY_MS = 250;
export const COMMERCIAL_ACCESS_MAX_TIMER_DELAY_MS = 2_147_000_000;
export const COMMERCIAL_ACCESS_MIN_RETRY_MS = 15_000;
export const COMMERCIAL_ACCESS_MAX_RETRY_MS = 60_000;

export type CommercialAccessRefreshSchedule =
  | { action: "none"; reason: string }
  | { action: "refreshNow"; reason: string }
  | {
      action: "schedule";
      delayMs: number;
      targetAt: string;
      isLongDelayChunk: boolean;
    };

function validTime(value: string | null): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function scheduled(delayMs: number, targetAt: string): CommercialAccessRefreshSchedule {
  const bounded = Math.min(Math.max(1, Math.ceil(delayMs)), COMMERCIAL_ACCESS_MAX_TIMER_DELAY_MS);
  return {
    action: "schedule",
    delayMs: bounded,
    targetAt,
    isLongDelayChunk: delayMs > COMMERCIAL_ACCESS_MAX_TIMER_DELAY_MS
  };
}

export function calculateCommercialAccessRefreshSchedule(input: {
  commercialAccess: CommercialAccessDto;
  clientNowMs: number;
  receivedAtMs: number;
  lastBoundaryRefreshAtMs?: number | null;
}): CommercialAccessRefreshSchedule {
  const access = input.commercialAccess;
  if (access.accessMode !== "full") return { action: "none", reason: "accessRestricted" };
  if (access.storedContractStatus !== "cancellationScheduled") {
    return { action: "none", reason: "noScheduledCancellation" };
  }

  const serviceEndsAtMs = validTime(access.serviceEndsAt);
  if (serviceEndsAtMs === null) return { action: "none", reason: "invalidServiceEndsAt" };
  const hasServerTiming = access.evaluatedAt !== null || access.nextRecheckAt !== null;
  const evaluatedAtMs = validTime(access.evaluatedAt);
  const nextRecheckAtMs = validTime(access.nextRecheckAt);
  if (hasServerTiming && (evaluatedAtMs === null || nextRecheckAtMs === null)) {
    return { action: "none", reason: "invalidServerTiming" };
  }

  let remainingMs: number;
  let targetAt: string;
  if (evaluatedAtMs !== null && nextRecheckAtMs !== null) {
    const elapsedSinceReceipt = Math.max(0, input.clientNowMs - input.receivedAtMs);
    remainingMs = nextRecheckAtMs - evaluatedAtMs - elapsedSinceReceipt;
    targetAt = new Date(nextRecheckAtMs).toISOString();
  } else {
    remainingMs = serviceEndsAtMs - input.clientNowMs;
    targetAt = new Date(serviceEndsAtMs).toISOString();
  }

  if (remainingMs <= 0) {
    const lastAttempt = input.lastBoundaryRefreshAtMs ?? null;
    if (lastAttempt !== null && input.clientNowMs - lastAttempt < COMMERCIAL_ACCESS_MIN_RETRY_MS) {
      return scheduled(
        COMMERCIAL_ACCESS_MIN_RETRY_MS - (input.clientNowMs - lastAttempt),
        targetAt
      );
    }
    return { action: "refreshNow", reason: "boundaryReached" };
  }
  return scheduled(remainingMs + COMMERCIAL_ACCESS_BOUNDARY_SAFETY_MS, targetAt);
}

export function commercialAccessRetryDelayMs(failureCount: number): number {
  const exponent = Math.max(0, Math.floor(failureCount) - 1);
  return Math.min(
    COMMERCIAL_ACCESS_MIN_RETRY_MS * (2 ** exponent),
    COMMERCIAL_ACCESS_MAX_RETRY_MS
  );
}

export function commercialAccessRedirectPath(input: {
  tenantSlug: string;
  pathname: string;
  accessMode: CommercialAccessDto["accessMode"];
}): string | null {
  if (input.accessMode === "full") return null;
  const contractPath = `/t/${input.tenantSlug}/teacher/admin/contract`;
  const endedPath = `/t/${input.tenantSlug}/service-ended`;
  const supportPath = `/t/${input.tenantSlug}/support`;
  if (input.pathname === supportPath) return null;
  if (input.accessMode === "contractOnly") {
    return input.pathname === contractPath ? null : contractPath;
  }
  return input.pathname === endedPath ? null : endedPath;
}

export function unauthenticatedEntryPath(input: {
  tenantSlug: string;
  pathname: string;
  search: string;
}): string {
  const returnTo = `${input.pathname}${input.search}`;
  return `/t/${input.tenantSlug}/entry?returnTo=${encodeURIComponent(returnTo)}`;
}

export function shouldRefreshForLifecycleEvent(
  event: "focus" | "visibilitychange" | "pageshow",
  visibilityState?: DocumentVisibilityState
): boolean {
  return event !== "visibilitychange" || visibilityState === "visible";
}

export type RefreshRequestGate = ReturnType<typeof createRefreshRequestGate>;

export function createRefreshRequestGate() {
  let generation = 0;
  const inFlight = new Map<string, Promise<{ value: unknown; stale: boolean; ticket: number }>>();
  return {
    beginForeground(): number {
      generation += 1;
      return generation;
    },
    isCurrent(ticket: number): boolean {
      return ticket === generation;
    },
    invalidate(): void {
      generation += 1;
      inFlight.clear();
    },
    runSilent<T>(tenantSlug: string, operation: () => Promise<T>): Promise<{ value: T; stale: boolean; ticket: number }> {
      const existing = inFlight.get(tenantSlug);
      if (existing) return existing as Promise<{ value: T; stale: boolean; ticket: number }>;
      generation += 1;
      const ticket = generation;
      const promise = operation()
        .then((value) => ({ value, stale: ticket !== generation, ticket }))
        .finally(() => {
          if (inFlight.get(tenantSlug) === promise) inFlight.delete(tenantSlug);
        });
      inFlight.set(tenantSlug, promise as Promise<{ value: unknown; stale: boolean; ticket: number }>);
      return promise;
    }
  };
}
